import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Red oak, about 30 in DBH, 60 ft, beside the driveway: climb and rig down in sections', amount: '$2,400' },
  { item: 'Two silver maples over the roof: deadwood and reduce limbs back 6 to 8 ft from the shingles', amount: '$900' },
  { item: 'Chip all brush and haul away; trunk wood cut to 18 in rounds and stacked by the garage', amount: '$300' },
  { item: 'Optional: grind the oak stump 6 in below grade and rake the grindings into the hole', amount: '$350' },
];

const faq = [
  {
    q: 'How do tree companies price a removal?',
    a: 'By the tree, not by the hour on paper. Size, lean, what is underneath it, whether it can be felled or must be rigged down, and how far the brush has to travel to the chipper all go into the number. Writing those factors on the quote helps the homeowner see why two oaks of the same height cost different amounts.',
  },
  {
    q: 'Is stump grinding included in tree removal?',
    a: 'Only if the quote says so. Offer it as a separate optional line with the grinding depth, and say what happens to the grindings. In Scopie the homeowner can tick it on the quote page and the total changes before they sign.',
  },
  {
    q: 'Can I book the site visit in Scopie before I quote?',
    a: 'Yes. Log the call as a request and book an assessment visit on your schedule. After you have walked the property, the quote is built for that request, so the call, the visit and the price stay together.',
  },
  {
    q: 'Does Scopie keep tree inventories or plant health care programs?',
    a: 'No. Scopie has no tree inventory, mapping or plant health care module. It covers the request, the assessment visit, the quote, the job and the invoice.',
  },
];

const page: IndustryPage = {
  slug: 'tree-service',
  order: 6,
  group: 'Outdoor',
  path: '/for/tree-service',
  primaryKeyword: 'tree service software',
  secondaryKeywords: ['tree service estimate software', 'how to price tree removal', 'arborist estimate app'],
  title: 'Tree Service Software: Per-Tree Estimates | Scopie', // 50
  metaDescription:
    'Price removals, trims and stump grinding tree by tree, say what happens to the wood, and let the homeowner add the stump before signing. For tree crews.', // 151
  h1: 'Tree service estimates, priced one tree at a time',
  answerFirst:
    'Scopie gives tree service crews a way to price every tree as its own line, with the rigging, cleanup and wood plan written out, and stump grinding offered as an add-on. Book the walk-around as an assessment visit, quote from it, and send the homeowner a link they can sign the same day.',
  exampleQuote: { label: 'Sample tree work estimate', lines, total: '$3,950', depositNote: 'Storm-season deposit and cancellation rules come from the crew\'s clause library.' },
  blocks: [
    {
      type: 'text',
      id: 'storm',
      h2: 'After a storm, the first clear estimate wins',
      lead: 'Half the street called you on Monday. By Wednesday they had three prices and a crew already cutting next door.',
      paragraphs: [
        'Storm work moves fast, and homeowners pick whoever answers clearly. A number scribbled on a business card does not hold up against an estimate that names each tree, says how it comes down and promises the brush leaves with the crew.',
        'Off-season work is different: dormant pruning in late winter, crown cleaning before spring growth, and removals scheduled around nesting season where that applies. Those jobs are planned weeks ahead, which makes a written estimate with a validity date just as important.',
      ],
    },
    {
      type: 'list',
      id: 'flow',
      h2: 'From the phone call to a signed estimate',
      style: 'steps',
      items: [
        { title: 'Log the request', text: 'Name, address, which trees and what worries the homeowner: the limb over the bedroom, the lean toward the fence.' },
        { title: 'Book the assessment', text: 'Put the walk-around on your schedule like any other visit, with travel time, so it does not collide with a crew day.' },
        { title: 'Price each tree', text: 'One line per tree or task, with size, location and method, plus cleanup and wood as their own lines.' },
        { title: 'Send the link', text: 'Copy the estimate link and text it. The homeowner reads it, ticks the stump grinding if they want it, and signs.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A typical residential estimate',
      label: 'Sample estimate: oak removal and maple reduction',
      lines,
      total: '$3,950',
      totalLabel: 'Total if the stump is ground',
      note: 'Example figures. The stump line is optional; without it the homeowner signs for the removal and pruning only.',
    },
    {
      type: 'list',
      id: 'exclusions',
      h2: 'Put these in the exclusions, every time',
      style: 'bullets',
      intro: 'Most arguments after tree work are about things nobody mentioned.',
      items: [
        { text: 'Ruts in a soft lawn from the chipper or the lift, and whether you lay mats.' },
        { text: 'Hidden metal, nails or concrete inside the trunk, and the cost of a ruined chain.' },
        { text: 'Root grinding beyond the stump, and surface roots left in place.' },
        { text: 'Permits for protected species or trees in the right of way, and who applies.' },
        { text: 'Damage to buried irrigation or lighting that was not marked.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Costly habits on tree estimates',
      style: 'mistakes',
      items: [
        { title: 'Pricing the tree, forgetting the drop zone', text: 'A tree over a pool or a greenhouse needs rigging and more hands. Price the access, not just the height.' },
        { title: 'Leaving the wood unsettled', text: 'Rounds stacked, split, or hauled? Homeowners have strong opinions, and the difference is hours.' },
        { title: 'Throwing in the stump', text: 'Grinding is real work with a separate machine. Offer it as its own line.' },
        { title: 'No crane clause', text: 'If a crane might be needed, say so and price it, or the margin vanishes on the day.' },
        { title: 'Verbal yes on storm jobs', text: 'Get the signature before the saw starts, especially when insurance may be involved.' },
      ],
    },
    {
      type: 'text',
      id: 'crew-day',
      h2: 'Crew days that match the machines',
      paragraphs: [
        'Once signed, the job goes on the schedule with the climbers and the equipment it needs: chipper, stump grinder, bucket truck or lift. The schedule shows which machine is already out on which day, and the crew sees addresses and times rather than prices. When the cleanup is done, the invoice goes out as a link, and the homeowner stays on file for the next storm.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Questions tree crews ask about estimates', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/for/fencing', anchor: 'Scopie for fence contractors', status: 'live' },
    { href: '/templates/tree-service-estimate-template', anchor: 'free tree service estimate template', status: 'live' },
  ],
  cta: {
    heading: 'Be the first estimate after the next storm',
    text: 'Save your per-tree items and exclusions in Scopie, and send the next estimate from the driveway.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
